// src/pages/notifications/ChannelEditPage.tsx
// Channel config editor — a settings-style surface (docs/ux/14-forms.md
// allows settings screens like channel config to keep their page layout),
// restructured to label-left FormRow instead of label-above stacked Input,
// with one atomic Save button.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsChannel, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Alert, ErrorAlert } from '@react/components';
import { FormRow, SegmentedControl } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { Select } from '@/components/Select';
import { validateChannelCreate, formatChannelType, formatChannelScope } from '@/features/notifications/logic';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { ChannelScopeValues } from '@core/types/notifications/ChannelScope';
import type { NotificationChannel } from '@core/types/notifications/NotificationChannel';
import type { ChannelEditFormState } from '@/features/notifications/types';

const scopeOptions = ChannelScopeValues.filter((v) => v !== 'CHANNEL_SCOPE_UNSPECIFIED').map((s) => {
  const sc = formatChannelScope(s);
  return { value: s, labelKey: sc.labelKey };
});

const webhookMethodOptions = [
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
];

export const ChannelEditPage = observer(() => {
  const store = useNotificationsChannel();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<ChannelEditFormState>({
    name: '', description: '', type: 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED', scope: 'CHANNEL_SCOPE_TENANT',
    isDefault: false, isEnabled: true,
    smtpHost: '', smtpPort: '587', smtpUsername: '', smtpPassword: '', useTls: true, fromEmail: '', fromName: '', replyTo: '',
    smsProvider: '', smsApiKey: '', smsApiSecret: '', smsSenderId: '',
    telegramBotToken: '', telegramWebhookUrl: '', telegramUseWebhook: false,
    slackBotToken: '', slackSigningSecret: '', slackDefaultChannel: '', slackWebhookUrl: '', slackUseBotToken: true,
    pushProvider: '', pushFcmServerKey: '', pushFcmProjectId: '', pushApnsKeyId: '', pushApnsTeamId: '', pushApnsBundleId: '', pushApnsProduction: false,
    webhookUrl: '', webhookMethod: 'POST', webhookSecretToken: '', webhookContentType: 'application/json', webhookVerifySsl: true,
  });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; message?: string } | null>(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => { if (id) store.GetChannel(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ch = store.currentItem as NotificationChannel | null;
    if (ch && !loaded) {
      setForm({
        name: ch.name ?? '', description: ch.description ?? '', type: ch.type ?? 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED', scope: ch.scope ?? 'CHANNEL_SCOPE_TENANT',
        isDefault: ch.is_default ?? false, isEnabled: ch.is_enabled ?? true,
        smtpHost: ch.email_config?.smtp_host ?? '', smtpPort: String(ch.email_config?.smtp_port ?? 587), smtpUsername: ch.email_config?.username ?? '', smtpPassword: ch.email_config?.password ?? '', useTls: ch.email_config?.use_tls ?? true, fromEmail: ch.email_config?.from_email ?? '', fromName: ch.email_config?.from_name ?? '', replyTo: ch.email_config?.reply_to ?? '',
        smsProvider: ch.sms_config?.provider ?? '', smsApiKey: ch.sms_config?.api_key ?? '', smsApiSecret: ch.sms_config?.api_secret ?? '', smsSenderId: ch.sms_config?.sender_id ?? '',
        telegramBotToken: ch.telegram_config?.bot_token ?? '', telegramWebhookUrl: ch.telegram_config?.webhook_url ?? '', telegramUseWebhook: ch.telegram_config?.use_webhook ?? false,
        slackBotToken: ch.slack_config?.bot_token ?? '', slackSigningSecret: ch.slack_config?.signing_secret ?? '', slackDefaultChannel: ch.slack_config?.default_channel ?? '', slackWebhookUrl: ch.slack_config?.webhook_url ?? '', slackUseBotToken: ch.slack_config?.use_bot_token ?? true,
        pushProvider: ch.push_config?.provider ?? '', pushFcmServerKey: ch.push_config?.fcm_server_key ?? '', pushFcmProjectId: ch.push_config?.fcm_project_id ?? '', pushApnsKeyId: ch.push_config?.apns_key_id ?? '', pushApnsTeamId: ch.push_config?.apns_team_id ?? '', pushApnsBundleId: ch.push_config?.apns_bundle_id ?? '', pushApnsProduction: ch.push_config?.apns_production ?? false,
        webhookUrl: ch.webhook_config?.url ?? '', webhookMethod: ch.webhook_config?.method ?? 'POST', webhookSecretToken: ch.webhook_config?.secret_token ?? '', webhookContentType: ch.webhook_config?.content_type ?? 'application/json', webhookVerifySsl: ch.webhook_config?.verify_ssl ?? true,
      });
      setLoaded(true);
    }
  }, [store.currentItem, loaded]);

  const set = <K extends keyof ChannelEditFormState>(field: K, value: ChannelEditFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    if (errors[field as string]) setErrors((prev) => ({ ...prev, [field as string]: '' }));
  };

  const handleChange = (field: keyof ChannelEditFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    set(field, e.target.value as never);

  const buildConfig = () => {
    switch (form.type) {
      case 'NOTIFICATION_CHANNEL_TYPE_EMAIL':
        return { email_config: { smtp_host: form.smtpHost, smtp_port: parseInt(form.smtpPort) || 587, username: form.smtpUsername, password: form.smtpPassword, use_tls: form.useTls, from_email: form.fromEmail, from_name: form.fromName, reply_to: form.replyTo } };
      case 'NOTIFICATION_CHANNEL_TYPE_SMS':
        return { sms_config: { provider: form.smsProvider, api_key: form.smsApiKey, api_secret: form.smsApiSecret, sender_id: form.smsSenderId } };
      case 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM':
        return { telegram_config: { bot_token: form.telegramBotToken, webhook_url: form.telegramWebhookUrl, use_webhook: form.telegramUseWebhook } };
      case 'NOTIFICATION_CHANNEL_TYPE_SLACK':
        return { slack_config: { bot_token: form.slackBotToken, signing_secret: form.slackSigningSecret, default_channel: form.slackDefaultChannel, webhook_url: form.slackWebhookUrl, use_bot_token: form.slackUseBotToken } };
      case 'NOTIFICATION_CHANNEL_TYPE_PUSH':
        return { push_config: { provider: form.pushProvider, fcm_server_key: form.pushFcmServerKey, fcm_project_id: form.pushFcmProjectId, apns_key_id: form.pushApnsKeyId, apns_team_id: form.pushApnsTeamId, apns_bundle_id: form.pushApnsBundleId, apns_production: form.pushApnsProduction } };
      case 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK':
        return { webhook_config: { url: form.webhookUrl, method: form.webhookMethod, secret_token: form.webhookSecretToken, content_type: form.webhookContentType, verify_ssl: form.webhookVerifySsl } };
      default:
        return {};
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const resp = await store.ValidateChannelConfig({ type: form.type, ...buildConfig() } as never);
      const r = resp as { is_valid?: boolean; error_message?: string } | null;
      setValidationResult({ valid: r?.is_valid ?? false, message: r?.error_message });
    } catch (err) {
      setValidationResult({ valid: false, message: err instanceof Error ? err.message : t('errors.unknown') });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    const validation = validateChannelCreate(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      const current = store.currentItem as NotificationChannel;
      await store.UpdateChannel(id ?? '', { ...current, name: form.name, description: form.description, type: form.type, scope: form.scope, is_default: form.isDefault, is_enabled: form.isEnabled, ...buildConfig() } as never);
      // Provider config (channels.UpdateChannelConfig) is a narrower,
      // dedicated endpoint separate from the channel metadata update above —
      // it can also run its own server-side validation via validate_config.
      await store.UpdateChannelConfig(id ?? '', { validate_config: false, ...buildConfig() } as never);
      setDirty(false);
      navigate(`/notifications/channels/${id}`);
    } catch (err) {
      setErrors({ form: err });
    }
  };

  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;

  const renderTypeConfig = () => {
    switch (form.type) {
      case 'NOTIFICATION_CHANNEL_TYPE_EMAIL':
        return (
          <>
            <FormRow label={t('notifications.smtpHost')} htmlFor="ch-smtp-host">
              <Input id="ch-smtp-host" value={form.smtpHost} onChange={handleChange('smtpHost')} error={errors.smtpHost as string} required />
            </FormRow>
            <FormRow label={t('notifications.smtpPort')} htmlFor="ch-smtp-port">
              <Input id="ch-smtp-port" type="number" value={form.smtpPort} onChange={handleChange('smtpPort')} className="w-28" />
            </FormRow>
            <FormRow label={t('notifications.smtpUsername')} htmlFor="ch-smtp-user">
              <Input id="ch-smtp-user" value={form.smtpUsername} onChange={handleChange('smtpUsername')} />
            </FormRow>
            <FormRow label={t('notifications.smtpPassword')} htmlFor="ch-smtp-pass">
              <Input id="ch-smtp-pass" type="password" value={form.smtpPassword} onChange={handleChange('smtpPassword')} />
            </FormRow>
            <FormRow label={t('notifications.useTls')}>
              <ToggleSwitch label="" checked={form.useTls} onChange={(v) => set('useTls', v)} />
            </FormRow>
            <FormRow label={t('notifications.fromEmail')} htmlFor="ch-from-email">
              <Input id="ch-from-email" type="email" value={form.fromEmail} onChange={handleChange('fromEmail')} error={errors.fromEmail as string} required />
            </FormRow>
            <FormRow label={t('notifications.fromName')} htmlFor="ch-from-name">
              <Input id="ch-from-name" value={form.fromName} onChange={handleChange('fromName')} />
            </FormRow>
            <FormRow label={t('notifications.replyTo')} htmlFor="ch-reply-to">
              <Input id="ch-reply-to" type="email" value={form.replyTo} onChange={handleChange('replyTo')} />
            </FormRow>
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_SMS':
        return (
          <>
            <FormRow label={t('notifications.smsApiKey')} htmlFor="ch-sms-key">
              <Input id="ch-sms-key" value={form.smsApiKey} onChange={handleChange('smsApiKey')} />
            </FormRow>
            <FormRow label={t('notifications.smsApiSecret')} htmlFor="ch-sms-secret">
              <Input id="ch-sms-secret" type="password" value={form.smsApiSecret} onChange={handleChange('smsApiSecret')} />
            </FormRow>
            <FormRow label={t('notifications.smsSenderId')} htmlFor="ch-sms-sender">
              <Input id="ch-sms-sender" value={form.smsSenderId} onChange={handleChange('smsSenderId')} />
            </FormRow>
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM':
        return (
          <>
            <FormRow label={t('notifications.botToken')} htmlFor="ch-tg-token">
              <Input id="ch-tg-token" value={form.telegramBotToken} onChange={handleChange('telegramBotToken')} error={errors.telegramBotToken as string} required />
            </FormRow>
            <FormRow label={t('notifications.webhookUrl')} htmlFor="ch-tg-webhook">
              <Input id="ch-tg-webhook" value={form.telegramWebhookUrl} onChange={handleChange('telegramWebhookUrl')} />
            </FormRow>
            <FormRow label={t('notifications.useWebhook')}>
              <ToggleSwitch label="" checked={form.telegramUseWebhook} onChange={(v) => set('telegramUseWebhook', v)} />
            </FormRow>
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_SLACK':
        return (
          <>
            <FormRow label={t('notifications.slackUseBotToken')}>
              <ToggleSwitch label="" checked={form.slackUseBotToken} onChange={(v) => set('slackUseBotToken', v)} />
            </FormRow>
            {form.slackUseBotToken ? (
              <>
                <FormRow label={t('notifications.slackBotToken')} htmlFor="ch-slack-token">
                  <Input id="ch-slack-token" value={form.slackBotToken} onChange={handleChange('slackBotToken')} error={errors.slackBotToken as string} />
                </FormRow>
                <FormRow label={t('notifications.slackSigningSecret')} htmlFor="ch-slack-secret">
                  <Input id="ch-slack-secret" type="password" value={form.slackSigningSecret} onChange={handleChange('slackSigningSecret')} />
                </FormRow>
                <FormRow label={t('notifications.slackDefaultChannel')} htmlFor="ch-slack-default">
                  <Input id="ch-slack-default" value={form.slackDefaultChannel} onChange={handleChange('slackDefaultChannel')} />
                </FormRow>
              </>
            ) : (
              <FormRow label={t('notifications.slackWebhookUrl')} htmlFor="ch-slack-webhook">
                <Input id="ch-slack-webhook" value={form.slackWebhookUrl} onChange={handleChange('slackWebhookUrl')} error={errors.slackBotToken as string} />
              </FormRow>
            )}
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_PUSH':
        return (
          <>
            <FormRow label={t('notifications.pushFcmServerKey')} htmlFor="ch-push-fcmkey">
              <Input id="ch-push-fcmkey" value={form.pushFcmServerKey} onChange={handleChange('pushFcmServerKey')} />
            </FormRow>
            <FormRow label={t('notifications.pushFcmProjectId')} htmlFor="ch-push-fcmproject">
              <Input id="ch-push-fcmproject" value={form.pushFcmProjectId} onChange={handleChange('pushFcmProjectId')} />
            </FormRow>
            <FormRow label={t('notifications.pushApnsKeyId')} htmlFor="ch-push-apnskey">
              <Input id="ch-push-apnskey" value={form.pushApnsKeyId} onChange={handleChange('pushApnsKeyId')} />
            </FormRow>
            <FormRow label={t('notifications.pushApnsTeamId')} htmlFor="ch-push-apnsteam">
              <Input id="ch-push-apnsteam" value={form.pushApnsTeamId} onChange={handleChange('pushApnsTeamId')} />
            </FormRow>
            <FormRow label={t('notifications.pushApnsBundleId')} htmlFor="ch-push-apnsbundle">
              <Input id="ch-push-apnsbundle" value={form.pushApnsBundleId} onChange={handleChange('pushApnsBundleId')} />
            </FormRow>
            <FormRow label={t('notifications.pushApnsProduction')}>
              <ToggleSwitch label="" checked={form.pushApnsProduction} onChange={(v) => set('pushApnsProduction', v)} />
            </FormRow>
          </>
        );
      case 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK':
        return (
          <>
            <FormRow label={t('notifications.webhookUrl')} htmlFor="ch-webhook-url">
              <Input id="ch-webhook-url" value={form.webhookUrl} onChange={handleChange('webhookUrl')} error={errors.webhookUrl as string} required />
            </FormRow>
            <FormRow label={t('notifications.webhookMethod')}>
              <SegmentedControl aria-label={t('notifications.webhookMethod')} value={form.webhookMethod} onChange={(v) => set('webhookMethod', v)} options={webhookMethodOptions} size="sm" />
            </FormRow>
            <FormRow label={t('notifications.webhookSecretToken')} htmlFor="ch-webhook-secret">
              <Input id="ch-webhook-secret" type="password" value={form.webhookSecretToken} onChange={handleChange('webhookSecretToken')} />
            </FormRow>
            <FormRow label={t('notifications.webhookContentType')} htmlFor="ch-webhook-content-type">
              <Input id="ch-webhook-content-type" value={form.webhookContentType} onChange={handleChange('webhookContentType')} />
            </FormRow>
            <FormRow label={t('notifications.webhookVerifySsl')}>
              <ToggleSwitch label="" checked={form.webhookVerifySsl} onChange={(v) => set('webhookVerifySsl', v)} />
            </FormRow>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/notifications/channels/${id}`} className="text-sm text-primary hover:text-primary/80">{t('common.back')}</Link>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('notifications.editChannel')}</h1>
      </div>

      {!!errors.form && (
        <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} />
      )}

      <Card>
        <CardHeader><CardTitle>{t('notifications.channelDetails')}</CardTitle></CardHeader>
        <CardContent>
          <FormRow label={t('common.name')} htmlFor="ch-name">
            <Input id="ch-name" value={form.name} onChange={handleChange('name')} error={errors.name as string} required autoCapitalize="off" />
          </FormRow>
          <FormRow label={t('common.description')} htmlFor="ch-description">
            <textarea
              id="ch-description"
              value={form.description}
              onChange={handleChange('description')}
              rows={2}
              autoCapitalize="off"
              placeholder={t('notifications.templateVariables.descriptionPlaceholder')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('notifications.channelType')}>
            <Select value={form.type} onChange={handleChange('type')} fullWidth={false} className="w-52">
              {NotificationChannelTypeValues.filter((v) => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((s) => {
                const ct = formatChannelType(s);
                return <option key={s} value={s}>{t(ct.labelKey)}</option>;
              })}
            </Select>
          </FormRow>
          <FormRow label={t('notifications.scope')}>
            <SegmentedControl aria-label={t('notifications.scope')} value={form.scope} onChange={(v) => set('scope', v)} options={scopeOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))} size="sm" />
          </FormRow>
          <FormRow label={t('notifications.enabled')}>
            <ToggleSwitch label="" checked={form.isEnabled} onChange={(v) => set('isEnabled', v)} />
          </FormRow>
          <FormRow label={t('notifications.default')}>
            <ToggleSwitch label="" checked={form.isDefault} onChange={(v) => set('isDefault', v)} />
          </FormRow>
        </CardContent>
      </Card>

      {form.type !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED' && (
        <Card>
          <CardHeader><CardTitle>{t(formatChannelType(form.type).labelKey)} {t('notifications.channelConfig')}</CardTitle></CardHeader>
          <CardContent>{renderTypeConfig()}</CardContent>
        </Card>
      )}

      {validationResult && (
        <Alert variant={validationResult.valid ? 'success' : 'error'} dismissible onDismiss={() => setValidationResult(null)}>
          {validationResult.valid ? t('notifications.configValid') : (validationResult.message || t('notifications.configInvalid'))}
        </Alert>
      )}

      <div className="flex gap-2">
        <Button variant="secondary" onClick={handleValidate} isLoading={validating}>{t('notifications.validateConfig')}</Button>
        <span className="flex-1" />
        <Button onClick={handleSave} isLoading={store.isSaving} disabled={!dirty}>{t('common.saveChanges')}</Button>
      </div>
    </div>
  );
});

export default ChannelEditPage;
